/* eslint-disable camelcase */
import { ReactNode, useMemo } from 'react'
import {
  Mutation,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AxiosError } from 'axios'
import { includes } from 'lodash'
import { overlay } from 'overlay-kit'
import { Dispatch } from 'redux'

import { useI18n } from '@ahha/i18n/i18n'
import { NotiCombinedData, TOAST_STATES } from '@ahha/utils/@types/types'
import Dialog from '@ahha/stableComponents/Dialog/Dialog'

import { setUser } from '@/stores/slices/userSlice'

import { API } from '@/api'
import { ErrorResponse } from '@/api/Base/types'
import { handleLicenseError } from '@/api/react-query/licenseErrorHandler'
import { HANDLED_UNIQUE_CODES, UNIQUE_CODES } from '@/api/react-query/const'
import { useGlobalNotification } from '@/context/notificationContext'
import { isLicenseError } from '@/utils/licenseError'

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: AxiosError<ErrorResponse, any>
  }
}

interface QueryProviderProps {
  children: ReactNode
}

interface CreateAppQueryClientParams {
  dispatch: Dispatch
  addNotification: (notification: NotiCombinedData) => void
  t: ReturnType<typeof useI18n>['t']
}

const getServerErrorMessage = ({ response }: AxiosError<ErrorResponse>) => {
  const { status_code, message } = response?.data ?? {}

  return message ?? 'Unknown Error'
}

const toastErrorMessage = (error: Error) => {
}

const mutationErrorLogger = (error: Error, mutation: Mutation, variables: unknown) => {
}

export const createAppQueryClient = ({
  dispatch,
  addNotification,
  t,
}: CreateAppQueryClientParams) => new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (handleLicenseError({ error, source: 'query', dispatch })) {
        return
      }

      const { enableErrorToast = true } = query.meta ?? {}
      if (!(error instanceof AxiosError)) {
        return
      }
      // FIXME: axios interceptor에서 처리 가능한지 확인
      if (error.response?.data?.unique_code === UNIQUE_CODES.USER_TOKEN_NOT_FOUND) {
        dispatch(setUser({
          token: '',
          user: {
            _id: '',
            email: '',
            name: '',
            lastWorkspaceId: '',
            meta: {},
            isSuperUser: false,
          },
          isSignIn: false,
        }))
        API().setToken('')
        window.location.href = '/login'
      }
      const message = getServerErrorMessage(error)
      console.error(message)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, _, mutation) => {
      if (handleLicenseError({ error, source: 'mutation', dispatch })) {
        return
      }

      const { unique_code: uniqueCode } = error?.response?.data || {}

      const { enableErrorToast = true } = mutation.meta ?? {}

      if ((uniqueCode === UNIQUE_CODES.PERMISSION_DENIED || uniqueCode === UNIQUE_CODES.READ_ONLY_USER) && (error.response?.config.method !== 'get')) {
        addNotification({
          title: t('common_noPermission_toast_title'),
          message: t('common_noPermission_toast_message'),
          status: TOAST_STATES.ERROR,
        })
      }

      // 크레딧 부족
      if (uniqueCode === UNIQUE_CODES.INSUFFICIENT_CREDIT) {
        overlay.open(({ isOpen, close }) => (
          <Dialog
            height="auto"
            open={isOpen}
            title={t('credit_error_dialog_insufficientCredit_title')}
            content={(<p style={{ fontSize: '0.875rem', lineHeight: '1.375rem' }}>{t('credit_error_dialog_insufficientCredit_message')}</p>)}
            onClose={close}
            showCloseButton
            confirmText={t('common_confirm')}
            confirmAction={close}
          />
        ))
        return
      }

      if (!(error instanceof AxiosError)) {
        return
      }
      const message = getServerErrorMessage(error)
      console.error(message)
      /* if (enableErrorToast) {
          toastErrorMessage(error)
        } */
    },
  }),
  defaultOptions: {
    queries: {
      // TODO: 바로 바뀌는 화면들을 봐야하는 경우가 더 많은것 같아 임시 주석처리
      // staleTime: 30 * 1000,
      retry: (count, error) => {
        if (isLicenseError(error)) {
          return false
        }

        const { status } = error?.response ?? {}

        if (status && status >= 500) {
          return false
        }
        return count < 1
      },
      throwOnError: (error) => !isLicenseError(error),
    },
    mutations: {
      throwOnError: (e) => {
        if (isLicenseError(e)) {
          return false
        }

        const { unique_code: uniqueCode } = e?.response?.data || {}
        return !includes(HANDLED_UNIQUE_CODES, uniqueCode)
      },
    },
  },
})

export const QueryProvider = ({ children }: QueryProviderProps) => {
  const { addNotification } = useGlobalNotification()
  const dispatch = useDispatch()
  const { t } = useI18n()

  const queryClient = useMemo(
    () => createAppQueryClient({
      dispatch,
      addNotification,
      t,
    }),
    []
  )

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      {children}
    </QueryClientProvider>
  )
}
